import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

const GOLD = '#c8a84b';
const DARK = '#0f1a2b';
const DARK2 = '#0a1220';
const CARD = 'rgba(255,255,255,0.04)';
const BORDER = 'rgba(255,255,255,0.08)';
const GREEN = '#4ade80';
const BLUE = '#0ea5e9';

const ChurchIcon = ({ size = 32 }: { size?: number }) => (
    <div style={{
        width: size, height: size, background: BLUE, borderRadius: size * 0.28,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        boxShadow: `0 4px 12px rgba(14,165,233,0.2)`
    }}>
        <svg width={size * 0.45} height={size * 0.55} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4V20M7 10H17" stroke="white" strokeWidth="3" strokeLinecap="round" />
        </svg>
    </div>
);

const getMC = (s: number) => s >= 80 ? GREEN : s >= 60 ? GOLD : '#f87171';
const fmt = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

interface MemberEnrollment {
    class_id: string;
    class_name: string;
    mastery_score: number;
    last_active_at: string | null;
    progress_state: any;
}

interface MemberSubjectProgress {
    id: string;
    category_name: string;
    current_unit: number | null;
    current_lesson: number | null;
    mastery_score: number | null;
    lessons_completed: string[];
    completed_at: string | null;
    last_activity_at: string | null;
}

const cardVariants = {
    hidden: { opacity: 0, y: 16 },
    visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.35 } }),
};

export default function ChurchMemberDetailPage() {
    const { memberId } = useParams<{ memberId: string }>();
    const { user, userProfile, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [memberName, setMemberName] = useState('Member');
    const [memberEmail, setMemberEmail] = useState('');
    const [enrollments, setEnrollments] = useState<MemberEnrollment[]>([]);
    const [subjectProgress, setSubjectProgress] = useState<MemberSubjectProgress[]>([]);
    const [loading, setLoading] = useState(true);

    // Auth guard: basic
    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate('/signin', { replace: true }); return; }
        if (!userProfile) return;
    }, [user, userProfile, authLoading, navigate]);

    useEffect(() => {
        if (!memberId || !userProfile?.facility_id) return;
        loadMemberData();
    }, [memberId, userProfile?.facility_id]);

    async function loadMemberData() {
        if (!memberId) return;
        setLoading(true);
        try {
            // Fetch member info
            const { data: memberUserRaw, error: memberErr } = await supabase
                .from('users')
                .select('name, email, sponsor_id')
                .eq('id', memberId)
                .single();

            const memberUser = memberUserRaw as any;

            if (memberErr || !memberUser) {
                navigate('/church/learn', { replace: true });
                return;
            }

            // Authorization check: Must be an admin OR the member's sponsor
            if (userProfile?.facility_role !== 'admin' && memberUser.sponsor_id !== user?.id) {
                navigate('/church/learn', { replace: true });
                return;
            }

            setMemberName(memberUser.name || t('church.admin.fallbackMember'));
            setMemberEmail(memberUser.email || '');

            // Fetch enrollments
            const { data: enrollData } = await supabase
                .from('class_enrollments')
                .select('class_id, mastery_score, last_active_at, progress_state, classes(name)')
                .eq('student_id', memberId);

            setEnrollments(((enrollData as any) || []).map((e: any) => ({
                class_id: e.class_id,
                class_name: e.classes?.name || 'Unknown',
                mastery_score: Number(e.mastery_score) || 0,
                last_active_at: e.last_active_at,
                progress_state: e.progress_state || {},
            })));

            // Fetch subject progress (no quiz details shown to admin)
            const { data: progressData } = await supabase
                .from('student_subject_progress')
                .select('id, category_id, current_unit, current_lesson, mastery_score, lessons_completed, completed_at, last_activity_at, categories(name)')
                .eq('user_id', memberId)
                .order('last_activity_at', { ascending: false });

            setSubjectProgress((progressData || []).map((p: any) => ({
                id: p.id,
                category_name: p.categories?.name || 'Unknown',
                current_unit: p.current_unit,
                current_lesson: p.current_lesson,
                mastery_score: p.mastery_score,
                lessons_completed: Array.isArray(p.lessons_completed) ? p.lessons_completed : [],
                completed_at: p.completed_at,
                last_activity_at: p.last_activity_at,
            })));
        } catch (err) {
            console.error('loadMemberData error:', err);
        } finally {
            setLoading(false);
        }
    }

    const totalLessons = enrollments.reduce((s, e) => s + (e.progress_state?.total_lessons_completed || 0), 0);
    const avgMastery = enrollments.length
        ? Math.round(enrollments.reduce((s, e) => s + e.mastery_score, 0) / enrollments.length)
        : 0;
    const completedCourses = subjectProgress.filter(sp => sp.completed_at).length;

    if (authLoading || loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: DARK2, flexDirection: 'column', gap: 16 }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                    style={{ width: 42, height: 42, borderRadius: '50%', border: '3px solid rgba(200,168,75,0.2)', borderTopColor: GOLD }}
                />
                <p style={{ color: GOLD, fontSize: 14, fontFamily: 'sans-serif' }}>{t('church.member_detail.loading')}</p>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ fontFamily: "'Inter', system-ui, sans-serif", background: DARK2, minHeight: '100vh', color: '#fff' }}
        >
            <div style={{ maxWidth: 1000, margin: '0 auto', padding: '28px 24px' }}>

                {/* BACK BUTTON + HEADER */}
                <div style={{ marginBottom: 28 }}>
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        onClick={() => navigate(userProfile?.facility_role === 'admin' ? '/church/admin' : '/church/learn')}
                        style={{
                            background: 'rgba(255,255,255,0.05)', border: `1px solid ${BORDER}`,
                            color: '#888', padding: '8px 16px', borderRadius: 10, cursor: 'pointer',
                            fontSize: 13, fontWeight: 600, marginBottom: 16,
                        }}
                    >
                        ← {t('church.member_detail.back_to', { to: userProfile?.facility_role === 'admin' ? t('church.member_detail.back_to_admin') : t('church.member_detail.back_to_dashboard') })}
                    </motion.button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <div style={{
                            width: 56, height: 56, background: 'rgba(200,168,75,0.15)',
                            borderRadius: '50%', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', color: GOLD, fontWeight: 700, fontSize: 22,
                        }}>
                            {memberName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>{memberName}</h1>
                            <p style={{ color: '#555', fontSize: 14, marginTop: 2 }}>{memberEmail}</p>
                        </div>
                    </div>
                </div>

                {/* STATS */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
                    {[
                        { icon: '📚', label: t('church.member_detail.stats.courses_enrolled'), value: enrollments.length, color: '#60a5fa' },
                        { icon: '✅', label: t('church.member_detail.stats.lessons_completed'), value: totalLessons, color: '#34d399' },
                        { icon: '🎯', label: t('church.member_detail.stats.avg_mastery'), value: `${avgMastery}%`, color: GOLD },
                        { icon: '🏆', label: t('church.member_detail.stats.courses_completed'), value: completedCourses, color: GREEN },
                    ].map((stat, i) => (
                        <motion.div
                            key={stat.label}
                            custom={i}
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '20px' }}
                        >
                            <div style={{ fontSize: 22, marginBottom: 8 }}>{stat.icon}</div>
                            <div style={{ fontSize: 28, fontWeight: 800, color: stat.color, letterSpacing: '-1px' }}>{stat.value}</div>
                            <div style={{ fontSize: 11, color: '#555', marginTop: 4 }}>{stat.label}</div>
                        </motion.div>
                    ))}
                </div>

                {/* COURSE ENROLLMENTS */}
                <div style={{ marginBottom: 32 }}>
                    <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📖 {t('church.member_detail.course_progress')}</h2>
                    {enrollments.length === 0 ? (
                        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '48px', textAlign: 'center', color: '#555' }}>
                            {t('church.member_detail.no_courses')}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {enrollments.map((enrol, idx) => {
                                const ps = enrol.progress_state || {};
                                return (
                                    <motion.div
                                        key={enrol.class_id}
                                        custom={idx}
                                        variants={cardVariants}
                                        initial="hidden"
                                        animate="visible"
                                        style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: '20px' }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                                            <div style={{ flexShrink: 0 }}>
                                                <ChurchIcon size={40} />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 700, fontSize: 15 }}>{enrol.class_name}</div>
                                                <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>
                                                    {t('church.member_detail.module_lesson', { unit: ps.current_unit || 1, lesson: ps.current_lesson || 1 })}
                                                    {' · '}{t('church.member_detail.lessons_done', { count: ps.total_lessons_completed || 0 })}
                                                    {' · '}{t('church.member_detail.last_active', { date: fmt(enrol.last_active_at) })}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontWeight: 800, fontSize: 22, color: getMC(enrol.mastery_score) }}>
                                                    {enrol.mastery_score}%
                                                </div>
                                                <div style={{ fontSize: 10, color: '#555' }}>{t('church.member_detail.mastery')}</div>
                                            </div>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 100, height: 8, overflow: 'hidden' }}>
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${Math.min((ps.total_lessons_completed || 0) * 12, 100)}%` }}
                                                transition={{ duration: 0.8 }}
                                                style={{ height: '100%', background: getMC(enrol.mastery_score), borderRadius: 100 }}
                                            />
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* SUBJECT PROGRESS */}
                {subjectProgress.length > 0 && (
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>📊 Subject Mastery</h2>
                        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead style={{ borderBottom: `1px solid ${BORDER}` }}>
                                    <tr>
                                        {['subject', 'position', 'lessons', 'MASTERY', 'status'].map(h => (
                                            <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#555', letterSpacing: '0.8px' }}>{t(`church.member_detail.table.${h}`)}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {subjectProgress.map((sp) => (
                                        <tr key={sp.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                                            <td style={{ padding: '14px 16px', fontWeight: 600, fontSize: 14 }}>{sp.category_name}</td>
                                            <td style={{ padding: '14px 16px', fontSize: 13, color: '#888' }}>
                                                {t('church.member_detail.table.unit_lesson', { unit: sp.current_unit || 1, lesson: sp.current_lesson || 1 })}
                                            </td>
                                            <td style={{ padding: '14px 16px', fontSize: 13 }}>
                                                {t('church.member_detail.table.done', {
                                                    count: Array.isArray(sp.lessons_completed)
                                                        ? new Set(sp.lessons_completed.map(t => typeof t === 'string' ? t.replace(/^u(\d+)_l(\d+)$/, '$1-$2') : t)).size
                                                        : 0
                                                })}
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                <span style={{
                                                    fontWeight: 800, fontSize: 14, color: getMC(sp.mastery_score || 0),
                                                }}>
                                                    {Math.round(sp.mastery_score || 0)}%
                                                </span>
                                            </td>
                                            <td style={{ padding: '14px 16px' }}>
                                                {sp.completed_at ? (
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: GREEN, background: 'rgba(74,222,128,0.15)', padding: '3px 10px', borderRadius: 100 }}>
                                                        ✅ {t('church.member_detail.table.complete')}
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#888', background: 'rgba(255,255,255,0.05)', padding: '3px 10px', borderRadius: 100 }}>
                                                        {t('church.member_detail.table.in_progress')}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </motion.div>
    );
}
